import { breakpoints } from '@naf/theme';

/**
 * `sizes` lists per layout group, for `ResponsiveImage`.
 *
 * `sizes` is call-site knowledge the component cannot derive — the browser resolves it before it
 * knows the layout, and a generic default over-fetches (`100vw` makes a third-width card request
 * the 1400 candidate). Hence one named entry per group rather than a literal per site.
 *
 * Media conditions are `@naf/theme` breakpoints; widths are the group's measured rendered width.
 * Re-measure before editing a width — issue #85 has the per-group table and the probe.
 */
export const IMAGE_SIZES = {
  /** Standard article hero. */
  articleHero: `(max-width: ${breakpoints.s}) 74vw, (max-width: ${breakpoints.xl}) 78vw, 928px`,
  /** Feature article hero — near-full-bleed. */
  featureHero: `(max-width: ${breakpoints.l}) 100vw, 89vw`,
  /** Member-benefit hero. */
  benefitHero: `(max-width: ${breakpoints.s}) 93vw, (max-width: 1440px) 96vw, 1400px`,
  /** NAF-centre hero. Same list as `bodyWide` today, but an independent group — keep them apart. */
  pageHero: `(max-width: ${breakpoints.l}) 100vw, 1120px`,
  /** Front-page hero. */
  frontPageHero: `(max-width: ${breakpoints.l}) 100vw, 1000px`,
  /** Article body, wide variant. */
  bodyWide: `(max-width: ${breakpoints.l}) 100vw, 1120px`,
  /** Article body, standard variant — also in-article blocks (poll, competition). */
  bodyStandard: `(max-width: ${breakpoints.s}) 89vw, (max-width: ${breakpoints.l}) 94vw, 736px`,
  /** Article body, CMS image block. */
  bodyBlock: `(max-width: ${breakpoints.l}) 92vw, 1120px`,
  /** Half-width body image — `grid-column: 3 / 8` of 12 in a 1120 body. */
  bodyHalf: `(max-width: ${breakpoints.m}) 90vw, 440px`,
  /** Two-column article layout. */
  twoColumn: `(max-width: ${breakpoints.m}) 90vw, (max-width: ${breakpoints.l}) 45vw, 544px`,
  /** Third-width card in a grid or carousel. */
  card: `(max-width: ${breakpoints.m}) 90vw, (max-width: ${breakpoints.l}) 30vw, 360px`,
  /** Fixed-width card in the image/title/link list block. */
  linkListCard: `(max-width: ${breakpoints.m}) 90vw, 389px`,
  /** Image carousel serializer — fixed widths, not fluid. */
  carousel: `(max-width: ${breakpoints.s}) 350px, 650px`,
  /** Edge-to-edge image, no container. */
  fullBleed: '100vw',
} as const;
